import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { listen } from "./tauri-web";
import { platform } from "./tauri-web";
import { supabase } from "./supabase";
import {
  store, computeStreak, computeBestStreak, computeStrength, streakCopy, todayISO, last30Days,
  type Habit, type Entry, type Cadence, type HeadlineFact,
} from "./store";
import { DayMark, MomentArt } from "./marks";
import {
  analyzeHabits, openWebLogin, BrainError,
  type HabitAnalysis,
} from "./brain";
import { OrioraBadge } from "./OrioraBadge";
import { BackupMenu } from "./BackupMenu";
// "The Punch Ledger" — the restructured home. See ledger.tsx for the a3 asks R1-R5.
import { Board, recoveryRecord, recoveryLine, cleanRecord, cleanLine, countStreakFor } from "./ledger";
import { WhyPrompt } from "./why";
import { loadReminder, saveReminder, syncReminder, type ReminderSetting } from "./reminders";
import { isTauri } from "./web-runtime";
import { AiConsent, AI_CONSENT_KEY, signInIos, signInMac } from "./ios-native-kit";
import { AccountMenu } from "./AccountMenu";
import { AddFundsMenu } from "./AddFundsMenu";
import Chat from "./chat/Chat";
import { PhotoButton, PhotoChip } from "./chat/photo";
import {
  Sparkles, Plus, ChevronLeft, Check, Trash2, Trophy, Archive,
  CalendarDays, WifiOff, X, RefreshCw, AlertCircle, Info,
} from "lucide-react";

const PALETTE = ["#D6336C", "#F59E0B", "#10B981", "#3B82F6", "#8B5CF6"];
const prettyCadence = (c: string) => (c === "daily" ? "Daily" : c === "weekly" ? "Weekly" : c === "adhoc" ? "As needed" : c);
const fmtDate = (s: string) =>
  s ? new Date(s.slice(0, 10) + "T00:00:00Z").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" }) : "";
// The last `n` calendar days ending today, oldest→newest — the home week-strip axis.
function lastDays(today: string, n: number): string[] {
  const base = new Date(today + "T00:00:00Z");
  return Array.from({ length: n }, (_, k) => {
    const d = new Date(base);
    d.setUTCDate(base.getUTCDate() - (n - 1 - k));
    return d.toISOString().slice(0, 10);
  });
}
const weekdayNarrow = (d: string) => new Date(d + "T00:00:00Z").toLocaleDateString("en-US", { weekday: "narrow", timeZone: "UTC" });
// SCREENSHOT=0 — sim-only auth bypass for identity screenshots. MUST stay 0 in committed/shipped code.
const SCREENSHOT = 0;

/**
 * check-ins that hold together as a streak — the link, not another tick.
 * The last of the shared lucide `Sparkles` launcher glyphs — 20 of the fleet's 26 chat launchers
 * rendered it in common, which is the shared-fingerprint half of Guideline 4.3(a). This app's
 * OUTLINE was already its own, so only the glyph needed to move.
 */
function ChainMark({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M10.4 13.6a3.6 3.6 0 0 1 0-5.1l1.4-1.4a3.6 3.6 0 1 1 5.1 5.1l-.8.8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <path d="M13.6 10.4a3.6 3.6 0 0 1 0 5.1l-1.4 1.4a3.6 3.6 0 1 1-5.1-5.1l.8-.8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

export default function App() {
  const [mounted, setMounted] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  // AI-consent gate (#62 / Apple 5.1.1i) — shown ONCE on first launch before anything else.
  const [aiConsent, setAiConsent] = useState(() => localStorage.getItem(AI_CONSENT_KEY) === "1");

  useEffect(() => {
    // onAuthStateChange fires INITIAL_SESSION after async filesystem storage loads —
    // avoids the cold-launch race where getSession() returned null before storage was ready.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setAuthed(session !== null);
      setEmail(session?.user?.email ?? null);
      setMounted(true);
    });
    const timeout = setTimeout(() => setMounted(true), 3000);
    return () => { clearTimeout(timeout); subscription.unsubscribe(); };
  }, []);

  // Deep-link auth: web login returns solo-habit-tracker://auth#access_token=…&refresh_token=…
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("auth:deep-link", async (e) => {
      try {
        const u = new URL(e.payload);
        const frag = new URLSearchParams(u.hash.replace(/^#/, ""));
        const access_token = frag.get("access_token");
        const refresh_token = frag.get("refresh_token");
        if (access_token && refresh_token) await supabase.auth.setSession({ access_token, refresh_token });
      } catch (err) {
        console.error("deep-link auth failed", err);
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  if (!aiConsent && !SCREENSHOT) {
    return (
      <AiConsent
        brand="Oriora Habits"
        onAccept={() => { localStorage.setItem(AI_CONSENT_KEY, "1"); setAiConsent(true); }}
      />
    );
  }
  if (!mounted && !SCREENSHOT) return <div style={{ height: "100%", display: "grid", placeItems: "center" }} className="muted">Loading…</div>;
  if (!authed && !SCREENSHOT) return <Login />;
  return <Dashboard email={SCREENSHOT ? "you@oriora.app" : email} />;
}

function Login() {
  const [busy, setBusy] = useState(false);
  return (
    <main style={{ height: "100dvh", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch" as any, boxSizing: "border-box", padding: "max(1.5rem, env(safe-area-inset-top)) 1.5rem max(1.5rem, env(safe-area-inset-bottom))" }}>
      <div className="card" style={{ width: "100%", maxWidth: 380, padding: "2rem", textAlign: "center", margin: "auto" }}>
        <div style={{ color: "var(--accent)", fontWeight: 700, fontSize: "var(--fs-sm)" }}>Oriora Habits</div>
        <h1 style={{ fontSize: "1.4rem", margin: "0.5rem 0 0.4rem" }}>Sign in with Oriora</h1>
        <p className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.5, marginBottom: "1.25rem" }}>
          Uses your Oriora account to sign in, then returns here. Your habits live on this
          device; AI insight runs on your Oriora account. Oriora doesn't retain what you send for an AI action. You can also back up to your own GitHub or Google Drive.
        </p>
        <button className="btn btn-primary" style={{ width: "100%" }} disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              // iOS: in-app OAuth via ASWebAuthenticationSession (#61, Guideline 4).
              // Desktop: open the system browser, session returns via deep link.
              const p = platform();
              if (p === "ios") await signInIos("solo-habit-tracker");
              else if (p === "macos") await signInMac("solo-habit-tracker");
              // Web: same-origin login. Go to the site login and return to this app's path; the
              // Supabase session is shared on the orioralabs.com origin, so we come back signed in.
              else if (p === "web") window.location.href = "/login?next=/solo-habit-tracker/";
              else await openWebLogin();
            } finally { setBusy(false); }
          }}>
          {busy ? "Opening sign-in…" : "Sign in with Oriora"}
        </button>
      </div>
    </main>
  );
}

function Dashboard({ email }: { email: string | null }) {
  const [habits, setHabits] = useState<Habit[]>([]);
  // Per-habit check-in history, keyed by habit id — powers the home streak + week-strip + the
  // tap-to-check-today ring without opening the detail view (the habit-app domain signature).
  const [entriesByHabit, setEntriesByHabit] = useState<Record<string, Entry[]>>({});

  /**
   * Ledger row 2b — ORDERING. `list_habits` serves `ORDER BY h.created_at ASC`, so the list was in
   * the order you happened to create things: a habit already ticked off today sat above one you
   * have not touched. The screen answers "what exists" when the only question a habit tracker is
   * opened with is "what still needs me today".
   *
   * Not-done-today first (that is the whole question), and within that the longest live streak
   * leads — the one with most to lose from a missed day. Done-today drop to the bottom, still
   * visible, ordered the same way.
   *
   * ⚠️ ORDERING ONLY, NO NUDGE. Nothing here scolds, colours red, or says "you should" — this app
   * states what the record shows. Putting the undone ones first is a sort, not a prompt.
   */
  const rankedHabits = useMemo(() => {
    const today = todayISO();
    const scored = habits.map((h) => {
      const dates = (entriesByHabit[h.id] ?? []).map((e) => e.logged_on.slice(0, 10));
      return { h, doneToday: dates.includes(today), streak: computeStreak(dates) };
    });
    return scored
      .sort((a, b) =>
        Number(a.doneToday) - Number(b.doneToday)      // not done today first
        || b.streak - a.streak                          // then the longest live run
        || a.h.name.localeCompare(b.h.name, undefined, { sensitivity: "base" }))
      .map((x) => x.h);
  }, [habits, entriesByHabit]);
  const [openId, setOpenId] = useState<string | null>(null); // habit detail view
  const [adding, setAdding] = useState<{ suggested?: string } | null>(null);
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && navigator.onLine === false);
  // Bumped to ask Insights to run an analysis (e.g. from the native ⌘I menu item).
  const [analyzeTick, setAnalyzeTick] = useState(0);
  // Chat panel (ADDITIVE): a natural-language front door over the SAME capabilities — does not
  // change any existing screen. onChanged → reload() refreshes the list after a chat-driven change.
  const [chatOpen, setChatOpen] = useState(false);

  const reload = useCallback(async () => {
    const hs = await store.listHabits();
    setHabits(hs);
    // Pull each habit's check-ins in parallel so the home can render live streaks/rings.
    const pairs = await Promise.all(hs.map(async (h) => [h.id, await store.listEntries(h.id)] as const));
    setEntriesByHabit(Object.fromEntries(pairs));
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  useEffect(() => {
    const on = () => setOffline(false), off = () => setOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  // Native menu actions (⌘N / ⌘I) → app state. Mirrors expense_audit_ai; without this the
  // menu items in lib.rs are dead. "analyze" only makes sense on the list view, not a detail.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("menu:action", (e) => {
      if (e.payload === "add") { setOpenId(null); setAdding({}); }
      else if (e.payload === "analyze") { setOpenId(null); setAnalyzeTick((t) => t + 1); }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  const openHabit = habits.find((h) => h.id === openId) ?? null;

  return (
    <main style={{ maxWidth: 720, margin: "0 auto", padding: "max(2rem, env(safe-area-inset-top)) max(1.5rem, env(safe-area-inset-right)) max(4rem, env(safe-area-inset-bottom)) max(1.5rem, env(safe-area-inset-left))" }}>
      {offline && (
        <div role="status" className="row" style={{ gap: "0.45rem", fontSize: "var(--fs-xs)", color: "var(--amber)", background: "color-mix(in srgb, var(--amber) 12%, transparent)", border: "1px solid color-mix(in srgb, var(--amber) 35%, transparent)", borderRadius: 8, padding: "0.45rem 0.75rem", marginBottom: "1rem" }}>
          <WifiOff size={14} style={{ flexShrink: 0 }} /> AI insight paused — check-ins still save locally
        </div>
      )}

      <header className="row" style={{ justifyContent: "space-between", marginBottom: "1.5rem" }}>
        <div className="row" style={{ gap: "0.5rem" }}>
          {openHabit && (
            <button className="btn" style={{ padding: "0.3rem 0.5rem", display: "inline-flex", alignItems: "center" }} title="Back" aria-label="Back" onClick={() => setOpenId(null)}>
              <ChevronLeft size={16} />
            </button>
          )}
          {/* R6 — the big app-name + email block was one of the four things the sibling test
              flagged (Mood and Stash carry the same header). On the home it is now a quiet
              label; on a habit's own page the habit's name is the title, which is correct. */}
          <div>
            {openHabit ? (
              <div style={{ fontSize: "1.25rem", fontWeight: 800, lineHeight: 1.1 }}>{openHabit.name}</div>
            ) : (
              <div className="muted" style={{ fontSize: "var(--fs-xs)", letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 650 }}>
                Oriora Habits
              </div>
            )}
          </div>
        </div>
        <div className="row" style={{ gap: "0.5rem", alignItems: "center" }}>
          <BackupMenu
            appName="Habit Tracker" fileSlug="habit-tracker" appScheme="solo-habit-tracker"
            hasData={habits.length > 0} onRestored={reload}
          />
          <AccountMenu email={email} appSlug="solo-habit-tracker" appName="Oriora Habits" />
          <AddFundsMenu />
        </div>
      </header>

      {openHabit ? (
        <HabitDetail habit={openHabit} onChange={() => { void reload(); }} onDeleted={() => { setOpenId(null); void reload(); }} />
      ) : (
        <>
          {habits.length === 0 ? (
            <EmptyState onPick={(name) => setAdding({ suggested: name })} onNew={() => setAdding({})}
              onStart={async (name) => {
                await store.createHabit({ name, cadence_target: "daily", color: PALETTE[0] });
                await reload();
              }} />
          ) : (
            <>
              {/* R1 — THE RECORD OF DAYS LEADS. R2 — today is markable here, one tap per habit. */}
              <Board habits={rankedHabits} entriesByHabit={entriesByHabit} onChanged={reload} />
              {/* One question, only when there is a real unanswered miss. Sits under the board
                  because it is ABOUT the board, and disappears the moment it is answered. */}
              <WhyPrompt habits={rankedHabits} entriesByHabit={entriesByHabit} today={todayISO()} onRecorded={reload} />

              {/* R4 — the AI panel is DEMOTED below the record. Analysis is a consequence of the
                  data, not the thing standing between the user and marking their day. */}
              <Insights habits={habits} analyzeTick={analyzeTick} />

              {/* The per-habit rows stay, as the way into a habit's own history — but they are no
                  longer the home's lead. */}
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.6rem", marginTop: "var(--space-4)" }}>
                {rankedHabits.map((h) => (
                  <HabitRow key={h.id} habit={h} entries={entriesByHabit[h.id] ?? []}
                    onOpen={() => setOpenId(h.id)} onToggled={reload} />
                ))}
              </div>
            </>
          )}
          {habits.length > 0 && (
            <button className="btn btn-primary row" style={{ marginTop: "1rem", gap: "0.4rem" }} onClick={() => setAdding({})}>
              <Plus size={16} /> Add a habit
            </button>
          )}
          {habits.length > 0 && <DailyNudge />}
          <ArchivedShelf onRestored={reload} />
        </>
      )}

      {adding && <NewHabitModal suggested={adding.suggested} onClose={() => setAdding(null)} onCreated={() => { setAdding(null); void reload(); }} />}

      {/* Chat panel — a natural-language way to use the app by typing. Purely additive: the launcher
          + slide-over live on top; no existing screen is altered. A chat-driven change reloads the list. */}
      {!chatOpen && (
        <button className="ohc-launch" onClick={() => setChatOpen(true)} aria-label="Open chat assistant">
          <ChainMark size={16} /> Chat
        </button>
      )}
      <aside className={`ohc-sheet ${chatOpen ? "open" : ""}`} aria-hidden={!chatOpen}>
        <div className="ohc-head">
          <span className="row" style={{ gap: "0.35rem" }}><Sparkles size={14} /> Assistant — type to add, check in, or recap</span>
          <button className="link" onClick={() => setChatOpen(false)}>Close ↓</button>
        </div>
        <Chat onChanged={reload} />
      </aside>
    </main>
  );
}

// Home habit card — the domain signature of a habit tracker (Productive/Habitify): a tap-to-check
// ring for TODAY, the current streak, and a seven-day strip, all without leaving the list. The info
// column opens the full detail; the ring toggles today's check-in in place.
/**
 * ONE daily nudge to fill the board in — not one reminder per habit.
 *
 * Streaks pings per task; our home IS the board and the whole day is marked in one sitting, so
 * eight pings would fight the product's own shape. Fired by the OS on this device: nothing about
 * the reminder, the time, or the habits leaves the phone.
 */
function DailyNudge() {
  const [setting, setSetting] = useState<ReminderSetting>(loadReminder);
  const [msg, setMsg] = useState<string | null>(null);

  // Re-lay the schedule each launch so the fortnight ahead stays filled.
  useEffect(() => { if (setting.enabled) void syncReminder(setting); /* eslint-disable-next-line */ }, []);

  if (!isTauri()) return null; // no notification plugin in the browser — never offer a dead switch

  const apply = async (next: ReminderSetting) => {
    setSetting(next);
    saveReminder(next);
    const on = await syncReminder(next);
    setMsg(
      !next.enabled ? "Reminder off."
      : on ? `We'll nudge you at ${next.time}, on this device.`
      : "Notifications are off for this app — turn them on in Settings to be reminded.",
    );
  };

  return (
    <div className="row" style={{ gap: "0.45rem", alignItems: "center", flexWrap: "wrap", marginTop: "var(--space-4)" }}>
      <label className="muted" style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", fontSize: "var(--fs-sm)" }}>
        <input
          type="checkbox"
          checked={setting.enabled}
          onChange={(ev) => void apply({ ...setting, enabled: ev.target.checked })}
          aria-label="Remind me to fill in the board"
        />
        Remind me to fill in the board
      </label>
      {setting.enabled && (
        <input type="time" value={setting.time} aria-label="Reminder time"
          onChange={(ev) => void apply({ ...setting, time: ev.target.value })} />
      )}
      {msg && <span className="muted" style={{ fontSize: "var(--fs-xs)" }}>{msg}</span>}
    </div>
  );
}

function HabitRow({ habit, entries, onOpen, onToggled }:
  { habit: Habit; entries: Entry[]; onOpen: () => void; onToggled: () => Promise<void> | void }) {
  const [busy, setBusy] = useState(false);
  const today = todayISO();
  const loggedSet = useMemo(() => new Set(entries.map((e) => e.logged_on.slice(0, 10))), [entries]);
  // polarity-aware: on a break habit the streak is the days CLEAN, not the days marked.
  const streak = useMemo(() => countStreakFor(entries, todayISO(), habit.polarity, habit.created_at), [entries, habit]);
  const week = useMemo(() => lastDays(today, 7), [today]);
  const doneToday = loggedSet.has(today);

  async function toggle(e: React.MouseEvent) {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      if (doneToday) {
        const todayEntry = entries.find((x) => x.logged_on.slice(0, 10) === today);
        if (todayEntry) await store.deleteEntry(todayEntry.id);
      } else {
        await store.logEntry(habit.id, today, null);
      }
      await onToggled();
    } finally { setBusy(false); }
  }

  return (
    <div className="card" style={{ padding: "0.9rem 1rem", display: "flex", gap: "0.85rem", alignItems: "center" }}>
      <button onClick={onOpen}
        style={{ flex: 1, minWidth: 0, textAlign: "left", background: "transparent", border: "none", padding: 0, cursor: "pointer", color: "inherit", font: "inherit" }}>
        <div className="row" style={{ gap: 8, alignItems: "center" }}>
          <span style={{ width: 12, height: 12, borderRadius: "50%", background: habit.color, flexShrink: 0 }} />
          <span style={{ fontWeight: 700, fontSize: "1.02rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{habit.name}</span>
        </div>
        <div className="row" style={{ gap: 8, alignItems: "center", marginTop: 3 }}>
          <span className="muted" style={{ fontSize: "var(--fs-xs)" }}>{prettyCadence(habit.cadence_target)}</span>
          {streak > 0 && <span style={{ fontSize: "var(--fs-xs)", fontWeight: 700, color: habit.color }}>🔥 {streak} day{streak === 1 ? "" : "s"}</span>}
        </div>
        {/* THE MOMENT PEOPLE QUIT. A streak at zero is where every competitor shows nothing; the
            customer's own record usually has something true and better to say. */}
        {streak === 0 && (habit.polarity === "break"
          ? cleanLine(cleanRecord(entries, today, habit.created_at))
          : recoveryLine(recoveryRecord(entries, today, habit.polarity))) && (
          <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 4, lineHeight: 1.35 }}>
            {habit.polarity === "break"
              ? cleanLine(cleanRecord(entries, today, habit.created_at))
              : recoveryLine(recoveryRecord(entries, today, habit.polarity))}
          </div>
        )}
        <div className="row" style={{ gap: 6, marginTop: 9 }}>
          {week.map((d) => {
            const on = loggedSet.has(d);
            const isToday = d === today;
            return (
              <div key={d} style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 3, justifyItems: "center" }}>
                <span style={{ width: 10, height: 10, borderRadius: "50%", background: on ? habit.color : "rgba(127,127,127,0.16)", outline: isToday ? `1.5px solid ${habit.color}` : "none", outlineOffset: 1.5 }} />
                <span className="muted" style={{ fontSize: 9, lineHeight: 1, opacity: isToday ? 1 : 0.6 }}>{weekdayNarrow(d)}</span>
              </div>
            );
          })}
        </div>
      </button>
      <button onClick={toggle} disabled={busy} aria-pressed={doneToday}
        aria-label={doneToday ? `Uncheck ${habit.name} for today` : `Check in ${habit.name} today`}
        style={{ width: 46, height: 46, borderRadius: "50%", flexShrink: 0, display: "grid", placeItems: "center", cursor: "pointer", padding: 0, transition: "background .15s, border-color .15s", border: `2px solid ${doneToday ? habit.color : "var(--border)"}`, background: doneToday ? habit.color : "transparent" }}>
        {doneToday ? <Check size={22} color="#fff" /> : <span style={{ width: 20, height: 20, borderRadius: "50%", border: `2px dashed color-mix(in srgb, ${habit.color} 55%, transparent)` }} />}
      </button>
    </div>
  );
}

// READY-STATE 2026-09-19 — this screen showed a 🌱 emoji and five chips that opened the add
// FORM, so the app's actual differentiator (PARITY-MATRIX: three day-states told apart by SHAPE,
// not colour, because eight habit colours sit side by side) was invisible until a habit existed,
// and the "one small thing" took a form to start. Two fixes, both using what the app already has:
// the delivered empty-state art from `marks.tsx` (MomentArt "empty-state" — a ledger row of
// dashed day-cells, shipped with the Punch Ledger identity and, until now, never rendered
// anywhere), and the REAL `DayMark` glyphs as the legend — not new shapes drawn for this screen.
function EmptyState({ onPick, onNew, onStart }: { onPick: (name: string) => void; onNew: () => void; onStart?: (name: string) => Promise<void> | void }) {
  const examples = ["Morning walk", "Read 10 minutes", "Drink water", "Stretch", "Journal"];
  const [busy, setBusy] = useState<string | null>(null);
  return (
    <div style={{ textAlign: "center", padding: "1.4rem 1rem 2rem" }}>
      <MomentArt id="empty-state" size={168} className="hb-empty-art" />
      <h2 style={{ margin: "0.9rem 0 0.25rem" }}>Start one small thing</h2>
      <p className="muted" style={{ fontSize: "var(--fs-sm)", maxWidth: 360, margin: "0 auto 1.15rem" }}>
        Every day you keep gets a mark in the ledger. There are three of them, and they are told
        apart by shape — so a row still reads at a glance with eight habits side by side.
      </p>

      <div className="hb-legend" aria-label="The three day-states">
        <div className="hb-leg"><DayMark state="done" size={22} /><span>Done</span></div>
        <div className="hb-leg"><DayMark state="missed" size={22} /><span>Missed</span></div>
        <div className="hb-leg"><DayMark state="skipped" size={22} /><span>Rest day</span></div>
      </div>
      <p className="hb-legend-note">
        A rest day you chose is a mark of its own, never a miss.
      </p>

      <div className="hb-pickrule"><span>Start one</span></div>
      <div className="row" style={{ flexWrap: "wrap", justifyContent: "center", gap: "0.5rem", marginBottom: "1rem" }}>
        {examples.map((ex) => (
          <button key={ex} className="btn" style={{ fontSize: "var(--fs-xs)" }} disabled={busy !== null}
            onClick={async () => {
              // ONE TAP: the chip now creates the habit and drops it into today's ledger row
              // instead of pre-filling a form. `Start something of my own` keeps the full form.
              if (!onStart) { onPick(ex); return; }
              setBusy(ex);
              try { await onStart(ex); } finally { setBusy(null); }
            }}>{busy === ex ? "Adding…" : ex}</button>
        ))}
      </div>
      <button className="btn btn-primary row" style={{ gap: "0.4rem", margin: "0 auto" }} onClick={onNew}>
        <Plus size={16} /> Start something of my own
      </button>
      <p className="hb-empty-foot">
        Everything stays on this device — a backup goes to your own cloud, not ours.
      </p>
    </div>
  );
}

function NewHabitModal({ onClose, onCreated, suggested }: { onClose: () => void; onCreated: () => void; suggested?: string }) {
  const [name, setName] = useState(suggested ?? "");
  const [polarity, setPolarity] = useState<"build" | "break">("build");
  const [schedKind, setSchedKind] = useState<"daily" | "days" | "count" | "adhoc">("daily");
  const [weekDays, setWeekDays] = useState<number[]>([1, 3, 5]); // Mon/Wed/Fri — the commonest pattern
  const [perWeek, setPerWeek] = useState(3);
  const [color, setColor] = useState(PALETTE[0]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true); setErr(null);
    try {
      // `schedule` is the real answer; cadence_target is kept in step so the existing
      // ranking/AI signals (which read cadence) keep working unchanged.
      const schedule =
        schedKind === "days"  ? (weekDays.length ? `days:${weekDays.join(",")}` : "daily")
      : schedKind === "count" ? `count:${perWeek}`
      : schedKind === "adhoc" ? "adhoc"
      : "daily";
      const cadence_target: Cadence =
        schedKind === "adhoc" ? "adhoc" : schedKind === "count" ? "weekly" : "daily";
      await store.createHabit({ name: name.trim(), cadence_target, schedule, polarity, color });
      onCreated();
    } catch (e) { setErr(e instanceof Error ? e.message : "Could not save"); setBusy(false); }
  }

  return (
    <ModalShell title="Add a habit" onCancel={onClose}>
      <form onSubmit={save}>
        <label className="muted" style={{ fontSize: "var(--fs-xs)" }}>What do you want to do?</label>
        <input autoFocus placeholder="Morning walk, read 10 minutes…" value={name} onChange={(e) => setName(e.target.value)} required style={{ marginBottom: "1rem" }} />

        {/* HOW OFTEN — "Mon/Wed/Fri" and "3x a week" are how habits are actually set (Streaks sells
            exactly this). It is not only a feature gap: until this shipped, a weekly habit was
            marked MISSED on the five days it was never owed. cadence_target is kept in step for the
            existing signals/AI, while `schedule` carries the real answer. */}
        {/* BUILD vs BREAK — Streaks sells "negative tasks", Way of Life sells "breaking bad ones".
            Same board, same gesture; only what a mark MEANS inverts. */}
        <label className="muted" style={{ fontSize: "var(--fs-xs)" }}>What kind of habit?</label>
        <div className="row" style={{ gap: "0.4rem", marginBottom: "1rem" }}>
          {([["build","Something I want to do"],["break","Something I want to stop"]] as const).map(([k, label]) => (
            <button type="button" key={k} className={polarity === k ? "btn btn-primary" : "btn"}
              style={{ flex: 1, fontSize: "var(--fs-xs)" }} onClick={() => setPolarity(k)}>
              {label}
            </button>
          ))}
        </div>
        {polarity === "break" && (
          <p className="muted" style={{ fontSize: "var(--fs-xs)", margin: "-0.6rem 0 1rem" }}>
            Mark a day when you slip. Your streak counts the days you stayed clean.
          </p>
        )}

        <label className="muted" style={{ fontSize: "var(--fs-xs)" }}>How often?</label>
        <div className="row" style={{ gap: "0.4rem", marginBottom: "0.6rem" }}>
          {([["daily","Every day"],["days","Certain days"],["count","Times a week"],["adhoc","Whenever"]] as const).map(([k, label]) => (
            <button type="button" key={k} className={schedKind === k ? "btn btn-primary" : "btn"}
              style={{ flex: 1, fontSize: "var(--fs-xs)" }} onClick={() => setSchedKind(k)}>
              {label}
            </button>
          ))}
        </div>
        {schedKind === "days" && (
          <div className="row" style={{ gap: "0.3rem", marginBottom: "1rem" }}>
            {["S","M","T","W","T","F","S"].map((d, i) => (
              <button type="button" key={i} aria-label={`Toggle day ${i}`} aria-pressed={weekDays.includes(i)}
                className={weekDays.includes(i) ? "btn btn-primary" : "btn"}
                style={{ flex: 1, fontSize: "var(--fs-xs)", padding: "0.4rem 0" }}
                onClick={() => setWeekDays((ds) => ds.includes(i) ? ds.filter((x) => x !== i) : [...ds, i].sort())}>
                {d}
              </button>
            ))}
          </div>
        )}
        {schedKind === "count" && (
          <div className="row" style={{ gap: "0.3rem", marginBottom: "1rem", alignItems: "center" }}>
            {[1,2,3,4,5,6].map((n) => (
              <button type="button" key={n} className={perWeek === n ? "btn btn-primary" : "btn"}
                style={{ flex: 1, fontSize: "var(--fs-xs)", padding: "0.4rem 0" }} onClick={() => setPerWeek(n)}>
                {n}×
              </button>
            ))}
            <span className="muted" style={{ fontSize: "var(--fs-xs)", whiteSpace: "nowrap" }}>a week</span>
          </div>
        )}

        <label className="muted" style={{ fontSize: "var(--fs-xs)" }}>Pick a colour</label>
        <div className="row" style={{ gap: "0.6rem", margin: "0.4rem 0 1rem" }}>
          {PALETTE.map((p) => (
            <button type="button" key={p} aria-label={`Colour ${p}`} onClick={() => setColor(p)}
              style={{ width: 32, height: 32, borderRadius: "50%", background: p, border: color === p ? "3px solid var(--ink-1)" : "3px solid transparent", display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
              {color === p && <Check size={14} color="#fff" />}
            </button>
          ))}
        </div>

        {err && <div style={{ fontSize: "var(--fs-xs)", color: "var(--rose)", marginBottom: "0.5rem" }}>{err}</div>}
        <div className="row" style={{ justifyContent: "flex-end", gap: "0.4rem" }}>
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? "Saving…" : "Add habit"}</button>
        </div>
      </form>
    </ModalShell>
  );
}

function HabitDetail({ habit, onChange, onDeleted }: { habit: Habit; onChange: () => void; onDeleted: () => void }) {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [checking, setChecking] = useState(false);
  const [bloom, setBloom] = useState(false);
  const [note, setNote] = useState("");
  const [photo, setPhoto] = useState("");      // proof photo staged for the check-in being written
  const [logForm, setLogForm] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [viewPhoto, setViewPhoto] = useState<Entry | null>(null); // lightbox for an existing entry's photo

  const reload = useCallback(async () => { setEntries(await store.listEntries(habit.id)); }, [habit.id]);
  useEffect(() => { void reload(); }, [reload]);

  const dates = useMemo(() => entries.map((e) => e.logged_on), [entries]);
  const streak = useMemo(() => countStreakFor(entries, todayISO(), habit.polarity, habit.created_at), [entries, habit]);
  const best = useMemo(() => computeBestStreak(dates), [dates]);
  const strength = useMemo(() => computeStrength(dates, habit.cadence_target), [dates, habit.cadence_target]);
  const loggedSet = useMemo(() => new Set(dates.map((d) => d.slice(0, 10))), [dates]);
  const today = todayISO();
  const loggedToday = loggedSet.has(today);
  const strip = useMemo(() => last30Days(today), [today]);

  async function checkIn(e?: React.FormEvent) {
    e?.preventDefault();
    setChecking(true); setErr(null);
    try {
      // photo "" → clears any existing photo for the day; a data-URL sets/replaces it. Optional.
      await store.logEntry(habit.id, today, note.trim() || null, "done", photo || "");
      setNote(""); setPhoto(""); setLogForm(false);
      await reload(); onChange();
      // The Bloom: quiet acknowledgment.
      setBloom(false);
      requestAnimationFrame(() => setBloom(true));
      setTimeout(() => setBloom(false), 500);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not check in");
    } finally { setChecking(false); }
  }

  async function removeEntry(id: string) {
    await store.deleteEntry(id);
    await reload(); onChange();
  }

  // Clear just the proof photo on an existing check-in, keeping the entry, its note and state.
  async function removePhoto(e: Entry) {
    await store.logEntry(habit.id, e.logged_on, null, e.state, "");
    setViewPhoto(null);
    await reload(); onChange();
  }

  return (
    <div>
      {bloom && <div className="bloom-ring" aria-hidden />}

      {/* ── Streak hero ── */}
      <div className="card" style={{ padding: "1.5rem", marginBottom: "1rem", background: "var(--accent-soft)", borderColor: "transparent" }}>
        <div className="row" style={{ justifyContent: "space-between" }}>
          <div>
            <div className="tnum" style={{ fontSize: "2rem", fontWeight: 800, lineHeight: 1.05, color: "var(--accent)" }}>
              {streak === 0 ? "Ready to start" : `${streak} ${streak === 1 ? "day" : "days"}`}
            </div>
            <div className="muted" style={{ fontSize: "var(--fs-sm)", marginTop: 4 }}>{streakCopy(streak)}</div>
            <div className="row" style={{ gap: 6, alignItems: "center", marginTop: 10, maxWidth: 190 }} title="Habit strength — builds with each check-in, fades when you miss">
              <div style={{ flex: 1, height: 6, borderRadius: 999, background: "rgba(127,127,127,0.18)", overflow: "hidden" }}>
                <div style={{ width: `${strength}%`, height: "100%", background: "var(--accent)", transition: "width .3s" }} />
              </div>
              <span className="tnum muted" style={{ fontSize: "var(--fs-xs)", whiteSpace: "nowrap" }}>{strength}% strong</span>
            </div>
          </div>
          {best > 0 && best > streak && (
            <div style={{ textAlign: "center" }}>
              <Trophy size={20} className="muted" />
              <div className="tnum" style={{ fontWeight: 700 }}>{best}</div>
              <div className="muted" style={{ fontSize: "var(--fs-xs)" }}>best</div>
            </div>
          )}
        </div>
      </div>

      {/* ── 30-day dot strip ── */}
      <div className="card" style={{ padding: "1rem 1.25rem", marginBottom: "1rem" }}>
        <div className="dotstrip">
          {strip.map((d) => {
            const isToday = d === today;
            const filled = loggedSet.has(d);
            return (
              <span key={d}
                className={`dot${filled ? " filled" : ""}${isToday ? " today" : ""}${isToday && filled && bloom ? " just-logged" : ""}`}
                style={{ borderColor: filled ? habit.color : undefined, background: filled ? habit.color : undefined }}
                title={d} />
            );
          })}
        </div>
        <div className="muted row" style={{ fontSize: "var(--fs-xs)", marginTop: "0.6rem", gap: "0.35rem" }}>
          <CalendarDays size={13} /> {habit.entries_30d === 0 ? "No check-ins this month — start today" : `${habit.entries_30d} check-in${habit.entries_30d === 1 ? "" : "s"} in the last 30 days`}
        </div>
      </div>

      {/* ── Check in ── */}
      {logForm ? (
        <form className="card" style={{ padding: "1rem 1.25rem", marginBottom: "1rem" }} onSubmit={checkIn}>
          <label className="muted" style={{ fontSize: "var(--fs-xs)" }}>How did it go? (optional)</label>
          <textarea rows={2} placeholder="Quick note for yourself…" value={note} onChange={(e) => setNote(e.target.value)} style={{ margin: "0.4rem 0 0.6rem" }} />
          {/* Optional PROOF PHOTO — a "photo streak". Stays on this device; nothing is uploaded. */}
          {photo
            ? <PhotoChip src={photo} onClear={() => setPhoto("")} />
            : (
              <div className="row" style={{ gap: "0.5rem", alignItems: "center", marginBottom: "0.4rem" }}>
                <PhotoButton onPhoto={setPhoto} disabled={checking} />
                <span className="muted" style={{ fontSize: "var(--fs-xs)" }}>Add a photo (optional)</span>
              </div>
            )}
          {err && <div style={{ fontSize: "var(--fs-xs)", color: "var(--rose)", marginBottom: "0.5rem" }}>{err}</div>}
          <div className="row" style={{ justifyContent: "flex-end", gap: "0.4rem" }}>
            <button type="button" className="btn" onClick={() => { setLogForm(false); setErr(null); setNote(""); setPhoto(""); }}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={checking}>{checking ? "Saving…" : "Save check-in"}</button>
          </div>
        </form>
      ) : (
        <button className="btn btn-primary row" style={{ width: "100%", justifyContent: "center", gap: "0.4rem", marginBottom: "1rem" }}
          disabled={checking} onClick={() => {
            if (!loggedToday) { void checkIn(); return; }
            // Editing today's existing check-in — prefill note + photo so saving amends rather
            // than wipes (photo="" on save would otherwise clear an already-attached photo).
            const todayEntry = entries.find((x) => x.logged_on.slice(0, 10) === today);
            setNote(todayEntry?.note ?? "");
            setPhoto(todayEntry?.photo_ref ?? "");
            setLogForm(true);
          }}>
          <Check size={16} /> {loggedToday ? "Add a note or photo for today" : checking ? "Checking in…" : "Check in today"}
        </button>
      )}
      {loggedToday && !logForm && <div className="muted" style={{ fontSize: "var(--fs-xs)", textAlign: "center", marginTop: "-0.6rem", marginBottom: "1rem" }}>You're checked in today ✓</div>}

      {/* ── Recent check-ins ── */}
      <div className="row" style={{ justifyContent: "space-between", margin: "0 0 0.5rem" }}>
        <h3 style={{ fontSize: "0.95rem", margin: 0 }}>Recent check-ins</h3>
        <span className="row" style={{ gap: "0.6rem" }}>
          {/* ARCHIVE sits BEFORE delete and is the plainer of the two. Until now, stopping a habit
              meant destroying a year of check-ins — the only reason anyone reached for Delete. */}
          <button className="btn" style={{ fontSize: "var(--fs-xs)", display: "inline-flex", alignItems: "center", gap: "0.3rem" }}
            onClick={async () => { await store.setArchived(habit.id, true); onDeleted(); }}>
            <Archive size={13} /> Archive
          </button>
          <button className="btn" style={{ fontSize: "var(--fs-xs)", color: "var(--rose)", display: "inline-flex", alignItems: "center", gap: "0.3rem" }} onClick={() => setConfirmDelete(true)}>
            <Trash2 size={13} /> Delete
          </button>
        </span>
      </div>
      {entries.length === 0 ? (
        <div className="card muted" style={{ padding: "1.5rem", textAlign: "center", fontSize: "var(--fs-sm)" }}>No check-ins yet — log your first today.</div>
      ) : (
        <div className="card" style={{ overflow: "hidden" }}>
          {entries.map((e, i) => (
            <div key={e.id} className="row" style={{ justifyContent: "space-between", padding: "0.7rem 1rem", borderTop: i === 0 ? "none" : "1px solid var(--border)" }}>
              <span className="row" style={{ gap: "0.6rem", minWidth: 0 }}>
                <span style={{ width: 24, height: 24, borderRadius: "50%", background: habit.color, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Check size={13} color="#fff" />
                </span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: "block", fontWeight: 500 }}>{fmtDate(e.logged_on)}</span>
                  {e.note && <span className="muted" style={{ fontSize: "var(--fs-xs)" }}>{e.note}</span>}
                </span>
              </span>
              <span className="row" style={{ gap: "0.5rem", alignItems: "center", flexShrink: 0 }}>
                {/* Proof-photo thumbnail — tap to view full-size (and remove) in a lightbox. */}
                {e.photo_ref && (
                  <button type="button" onClick={() => setViewPhoto(e)} title="View photo" aria-label="View check-in photo"
                    style={{ padding: 0, border: "none", background: "none", cursor: "pointer", lineHeight: 0, borderRadius: "var(--radius-sm, 8px)" }}>
                    <img src={e.photo_ref} alt="" style={{ width: 40, height: 40, objectFit: "cover", borderRadius: "var(--radius-sm, 8px)", display: "block" }} />
                  </button>
                )}
                <button className="btn" style={{ padding: "0.2rem 0.4rem", color: "var(--ink-3)", display: "inline-flex", alignItems: "center" }} title="Remove check-in" aria-label="Remove check-in" onClick={() => removeEntry(e.id)}>
                  <X size={14} />
                </button>
              </span>
            </div>
          ))}
        </div>
      )}

      {confirmDelete && (
        <ModalShell title={`Delete "${habit.name}"?`} onCancel={() => setConfirmDelete(false)}>
          <p className="muted" style={{ fontSize: "var(--fs-xs)", margin: "0 0 1rem" }}>
            This removes the habit and all its check-ins. This can't be undone.
          </p>
          <div className="row" style={{ justifyContent: "flex-end", gap: "0.4rem" }}>
            <button className="btn" onClick={() => setConfirmDelete(false)}>Cancel</button>
            <button className="btn btn-primary" style={{ background: "var(--rose)", borderColor: "var(--rose)" }}
              onClick={async () => { await store.deleteHabit(habit.id); onDeleted(); }}>Delete</button>
          </div>
        </ModalShell>
      )}

      {/* Proof-photo lightbox — full-size view with replace / remove. On-device only. */}
      {viewPhoto && (
        <ModalShell title={fmtDate(viewPhoto.logged_on)} onCancel={() => setViewPhoto(null)}>
          <img src={viewPhoto.photo_ref} alt="Check-in photo"
            style={{ width: "100%", borderRadius: "var(--radius-sm, 8px)", marginBottom: "0.9rem", maxHeight: "60vh", objectFit: "contain" }} />
          <div className="row" style={{ justifyContent: "space-between", gap: "0.4rem", alignItems: "center" }}>
            <PhotoButton
              onPhoto={async (dataUrl) => {
                // Replace the photo on this day's check-in, keeping its note and state.
                await store.logEntry(habit.id, viewPhoto.logged_on, null, viewPhoto.state, dataUrl);
                setViewPhoto(null);
                await reload(); onChange();
              }}
            />
            <span className="row" style={{ gap: "0.4rem" }}>
              <button className="btn" onClick={() => setViewPhoto(null)}>Close</button>
              <button className="btn" style={{ color: "var(--rose)" }} onClick={() => removePhoto(viewPhoto)}>Remove photo</button>
            </span>
          </div>
        </ModalShell>
      )}
    </div>
  );
}

// ── AI feature: habit-pattern analysis (the one brain call) ───────────────────
// Renders an honest streak summary + one observational insight (the server prompt is the
// guardrail: pattern-spotting, never a guilt-trip). Runs on the customer's Oriora account.
type InsightErr = { kind: "funds" | "auth" | "offline" | "generic"; message: string };

function isEmptyAnalysis(a: HabitAnalysis): boolean {
  const adh = a.adherence_by_habit && Object.keys(a.adherence_by_habit).length > 0;
  const obs = a.observations && a.observations.filter((o) => o && o.trim()).length > 0;
  return !a.raw && !a.streak_summary && !a.one_insight && !a.headline && !obs && !adh;
}

// The on-device facts — deterministic, grounded full-sentence reads of the user's own
// check-ins. Tone drives a subtle dot tint (good = accent, watch = amber, neutral = grey).
function FactList({ facts }: { facts: HeadlineFact[] }) {
  const dot = (t: string) => (t === "good" ? "var(--accent)" : t === "watch" ? "var(--amber)" : "var(--ink-3)");
  return (
    <ul style={{ listStyle: "none", margin: "0.5rem 0 0", padding: 0 }}>
      {facts.map((f, i) => (
        <li key={i} className="row obs-item" style={{ gap: "0.5rem", alignItems: "flex-start", marginBottom: "0.5rem" }}>
          <span style={{ width: 5, height: 5, borderRadius: "50%", background: dot(f.tone), flexShrink: 0, marginTop: 7 }} />
          <span style={{ fontSize: "var(--fs-sm)", color: "var(--ink-1)", lineHeight: 1.5 }}>{f.text}</span>
        </li>
      ))}
    </ul>
  );
}

function Insights({ habits, analyzeTick }: { habits: Habit[]; analyzeTick: number }) {
  const [analysis, setAnalysis] = useState<HabitAnalysis | null>(null);
  // On-device, deterministic ready-to-say facts (computed locally in Rust). Surfaced as a
  // grounded baseline — works offline, and fills in when the brain has nothing clean to add.
  const [facts, setFacts] = useState<HeadlineFact[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<InsightErr | null>(null);

  // Cache by the data signature so re-clicking with UNCHANGED data skips a redundant
  // call. Analysis is always an explicit, user-triggered action.
  const lastSig = useRef("");

  const totalCheckins = habits.reduce((s, h) => s + h.entries_30d, 0);
  const hasData = habits.length > 0 && totalCheckins > 0;

  const run = useCallback(async () => {
    setBusy(true); setErr(null);
    try {
      // Build the DERIVED-SIGNAL payload locally (raw dates stay on device). `today`
      // is the user's local day so the month/week boundaries match what's on screen.
      const payload = await store.signalsForAnalysis(todayISO());
      // The on-device facts are always shown — deterministic, grounded, no network needed.
      setFacts(payload.headline_facts ?? []);
      const sig = JSON.stringify(payload);
      // Already analyzed exactly this data — show the cached result, skip the call.
      if (sig === lastSig.current && analysis) { setBusy(false); return; }
      const r = await analyzeHabits(payload);
      setAnalysis(r.analysis);
      lastSig.current = sig;
    } catch (e) {
      if (e instanceof BrainError) {
        const kind: InsightErr["kind"] =
          e.code === "insufficient_funds" ? "funds"
          : e.code === "unauthenticated" ? "auth"
          : e.code === "unavailable" ? "offline"
          : "generic";
        setErr({ kind, message: e.message });
      } else {
        setErr({ kind: "generic", message: "Analysis failed — please try again." });
      }
    } finally { setBusy(false); }
  }, [analysis]);

  // Native ⌘I menu item (and the Dashboard "Analyze" trigger) bumps analyzeTick.
  const lastTick = useRef(0);
  useEffect(() => {
    if (analyzeTick > 0 && analyzeTick !== lastTick.current) {
      lastTick.current = analyzeTick;
      if (hasData && !busy) void run();
    }
  }, [analyzeTick, hasData, busy, run]);

  if (!hasData && !analysis && !err) return null;

  const empty = analysis !== null && isEmptyAnalysis(analysis);

  return (
    <div className="card" style={{ padding: "1rem 1.25rem", marginBottom: "1rem", borderLeft: "3px solid var(--accent)" }}>
      <div className="row" style={{ justifyContent: "space-between" }}>
        <div className="row" style={{ fontWeight: 600, fontSize: "var(--fs-sm)", gap: "0.35rem" }}>
          <Sparkles size={14} /> AI insights
        </div>
        <button className="btn row" style={{ fontSize: "var(--fs-xs)", gap: "0.3rem" }} disabled={busy || !hasData} onClick={() => void run()}>
          {busy ? <RefreshCw size={12} className="spin" /> : <Sparkles size={12} />}
          {busy ? "Analyzing…" : analysis || err ? "Re-analyze" : "Analyze my habits"}
        </button>
      </div>

      {/* First-run prompt (have data, never run yet, no error). */}
      {!busy && !analysis && !err && (
        <p className="muted" style={{ fontSize: "var(--fs-xs)", margin: "0.6rem 0 0", lineHeight: 1.5 }}>
          Reads your adherence vs. each habit's cadence, streak gaps, and how this month compares to last — then names 2-3 specific patterns. Runs on your Oriora account.
        </p>
      )}

      {/* Loading shimmer — the brain takes a moment. */}
      {busy && !analysis && (
        <div style={{ marginTop: "0.75rem" }}>
          <div className="skeleton" style={{ height: 10, width: "82%", marginBottom: 8 }} />
          <div className="skeleton" style={{ height: 10, width: "64%" }} />
        </div>
      )}

      {/* Error states — each actionable, never a dead end. The on-device facts still show
          below: they're computed locally and need no network or funds. */}
      {err && (
        <>
          <div className="row" style={{ marginTop: "0.7rem", gap: "0.5rem", alignItems: "flex-start" }}>
            <AlertCircle size={15} style={{ flexShrink: 0, marginTop: 1, color: err.kind === "funds" ? "var(--amber)" : "var(--ink-3)" }} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: "var(--fs-xs)", color: "var(--ink-2)", lineHeight: 1.5 }}>{err.message}</div>
              {err.kind === "auth" && (
                <button className="btn" style={{ fontSize: "var(--fs-xs)", marginTop: "0.45rem" }} onClick={() => void supabase.auth.signOut()}>Sign in again</button>
              )}
            </div>
          </div>
          {facts.length > 0 && (
            <div style={{ marginTop: "0.7rem", paddingTop: "0.7rem", borderTop: "1px solid var(--border)" }}>
              <div className="muted" style={{ fontSize: "var(--fs-xs)", marginBottom: "0.1rem" }}>From your check-ins, on this device:</div>
              <FactList facts={facts} />
            </div>
          )}
        </>
      )}

      {/* Empty-but-successful result — the model had nothing clear to ADD, but the grounded
          on-device facts still stand on their own. */}
      {empty && !err && (
        facts.length > 0 ? (
          <FactList facts={facts} />
        ) : (
          <p className="muted" style={{ fontSize: "var(--fs-sm)", margin: "0.7rem 0 0", lineHeight: 1.5 }}>
            Not enough of a pattern to call out yet — keep checking in for a few more days and re-run.
          </p>
        )
      )}

      {analysis && !empty && (
        <div style={{ marginTop: "0.75rem" }}>
          {analysis.raw ? (
            <p style={{ fontSize: "var(--fs-sm)", color: "var(--ink-2)", lineHeight: 1.55, margin: 0, whiteSpace: "pre-wrap" }}>{analysis.raw}</p>
          ) : (
            <>
              {analysis.adherence_by_habit && Object.keys(analysis.adherence_by_habit).length > 0 && (
                <div style={{ marginBottom: "0.6rem" }}>
                  {Object.entries(analysis.adherence_by_habit).map(([name, pct]) => (
                    <div key={name} style={{ marginBottom: "0.4rem" }}>
                      <div className="row" style={{ justifyContent: "space-between", fontSize: "var(--fs-xs)" }}>
                        <span style={{ color: "var(--ink-2)" }}>{name}</span>
                        <span className="tnum" style={{ fontWeight: 600 }}>{Math.round(Number(pct))}%</span>
                      </div>
                      <div style={{ height: 6, borderRadius: 999, background: "var(--elev-2)", overflow: "hidden", marginTop: 3 }}>
                        <div className="adh-fill" style={{ width: `${Math.max(0, Math.min(100, Number(pct)))}%`, height: "100%", background: "var(--accent)" }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {/* Optional one-line headline. */}
              {analysis.headline && (
                <p style={{ fontSize: "var(--fs-sm)", color: "var(--ink-1)", lineHeight: 1.5, margin: "0 0 0.5rem", fontWeight: 600 }}>{analysis.headline}</p>
              )}

              {/* The richer payload: specific, number-naming observations from the brain.
                  If the brain returned none, fall back to the on-device pre-ranked facts so
                  the user always gets grounded, full-sentence reads — never just bare bars. */}
              {analysis.observations && analysis.observations.filter((o) => o && o.trim()).length > 0 ? (
                <ul style={{ listStyle: "none", margin: "0 0 0.2rem", padding: 0 }}>
                  {analysis.observations.filter((o) => o && o.trim()).map((o, i) => (
                    <li key={i} className="row obs-item" style={{ gap: "0.5rem", alignItems: "flex-start", marginBottom: "0.5rem" }}>
                      <span style={{ width: 5, height: 5, borderRadius: "50%", background: "var(--accent)", flexShrink: 0, marginTop: 7 }} />
                      <span style={{ fontSize: "var(--fs-sm)", color: "var(--ink-1)", lineHeight: 1.5 }}>{o}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                facts.length > 0 && <FactList facts={facts} />
              )}

              {/* Legacy fields — still rendered if a back-level server returns them. */}
              {analysis.streak_summary && (
                <p style={{ fontSize: "var(--fs-sm)", color: "var(--ink-2)", lineHeight: 1.5, margin: "0 0 0.4rem" }}>{analysis.streak_summary}</p>
              )}
              {analysis.one_insight && (
                <p style={{ fontSize: "var(--fs-sm)", color: "var(--ink-1)", lineHeight: 1.5, margin: 0, fontWeight: 500 }}>{analysis.one_insight}</p>
              )}
            </>
          )}

          <div style={{ marginTop: 8 }}><OrioraBadge /></div>
          {/* Observational guardrail label — sets expectations: patterns, not advice. */}
          <div className="row muted" style={{ gap: "0.35rem", fontSize: 10, marginTop: "0.6rem", alignItems: "flex-start", lineHeight: 1.4 }}>
            <Info size={11} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>Observations only — describes patterns in your check-ins, not advice.</span>
          </div>
        </div>
      )}

    </div>
  );
}

/**
 * ARCHIVED — retired habits, and the way back.
 *
 * Hidden entirely when there are none, and collapsed when there are: an archived habit is by
 * definition not what the customer is working on, so it must not compete with the board. But
 * archiving with no way back is a trap, which is the only thing that would make it worse than
 * delete.
 */
function ArchivedShelf({ onRestored }: { onRestored: () => void | Promise<void> }) {
  const [rows, setRows] = useState<Habit[] | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const load = () => store.listArchived().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  if (!rows || rows.length === 0) return null;

  return (
    // The floating Chat bubble is a locked fleet standard and sits bottom-right over everything.
    // As the LAST thing on the page, this shelf's final row landed underneath it and its Restore
    // button could not be tapped — caught on device, invisible in any layout that is not full-height.
    <section style={{ marginTop: "1.25rem", paddingBottom: "5.5rem" }}>
      <button className="btn btn-ghost" style={{ fontSize: "var(--fs-xs)", padding: "0.25rem 0" }}
        onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        {open ? "Hide" : "Show"} archived ({rows.length})
      </button>
      {open && (
        <div className="card" style={{ overflow: "hidden", marginTop: "0.4rem" }}>
          {rows.map((h, i) => (
            <div key={h.id} className="row"
              style={{ justifyContent: "space-between", padding: "0.65rem 0.9rem", borderTop: i === 0 ? "none" : "1px solid var(--border)" }}>
              <span className="row" style={{ gap: "0.5rem", minWidth: 0 }}>
                <span style={{ width: 10, height: 10, borderRadius: "50%", background: h.color, flexShrink: 0 }} />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{h.name}</span>
              </span>
              <button className="btn" style={{ fontSize: "var(--fs-xs)" }} disabled={busy === h.id}
                onClick={async () => {
                  setBusy(h.id);
                  try { await store.setArchived(h.id, false); await load(); await onRestored(); }
                  finally { setBusy(null); }
                }}>
                Restore
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function ModalShell({ title, children, onCancel }: { title: string; children: React.ReactNode; onCancel: () => void }) {
  return (
    <div role="dialog" aria-modal="true" aria-label={title} onClick={onCancel}
      style={{ position: "fixed", inset: 0, background: "rgba(28,26,24,0.35)", display: "grid", placeItems: "center", zIndex: 50, padding: "1rem" }}>
      <div className="card" onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 360, padding: "1.25rem", boxShadow: "var(--shadow-2)" }}>
        <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", marginBottom: "0.75rem" }}>{title}</div>
        {children}
      </div>
    </div>
  );
}
